/**
 * Metpure link rules (snippets/mp-link-rules.liquid)
 *  1. Info links open in a pop-up that loads the live page/policy content (single source).
 *  2. Sign-in / sign-up links carry return_url back to the current page.
 *  3. Everything else is untouched.
 */
(function () {
  const modal = document.querySelector('[data-mp-info-modal]');
  if (!modal || modal.dataset.ready) return;
  modal.dataset.ready = 'true';

  const split = (value) => (value || '').split('|').map((p) => p.trim()).filter(Boolean);
  const infoPaths = split(modal.dataset.infoPaths);
  const authPaths = split(modal.dataset.authPaths);
  const loginPath = modal.dataset.loginUrl || '/account/login';
  const registerPath = modal.dataset.registerUrl || '/account/register';
  const titleEl = modal.querySelector('#MpInfoTitle');
  const bodyEl = modal.querySelector('[data-mp-info-body]');
  const cache = new Map();

  const here = () => window.location.pathname + window.location.search;
  const matches = (path, list) =>
    list.some((p) => (p.endsWith('/') ? path.startsWith(p) : path === p || path === p + '/'));

  /* ---------- Rule 2: sign-in / sign-up links return to this page ---------- */

  function withReturn(link) {
    let url;
    try {
      url = new URL(link.getAttribute('href'), window.location.origin);
    } catch (e) {
      return;
    }
    if (url.origin !== window.location.origin) return;
    const path = url.pathname.replace(/\/$/, '');

    // New customer accounts drop the return path on /account/register, but the login screen
    // offers sign-up too, so route sign-up links through login to keep the return.
    if (path === registerPath.replace(/\/$/, '')) url.pathname = loginPath;

    if (path === loginPath.replace(/\/$/, '') || path === registerPath.replace(/\/$/, '') || matches(path, authPaths)) {
      url.searchParams.set('return_url', here());
      link.setAttribute('href', url.pathname + url.search + url.hash);
      link.removeAttribute('target');
    }
  }

  function applyReturnUrls(scope) {
    scope.querySelectorAll('a[href]').forEach(withReturn);
  }

  /* ---------- Rule 1: info links open in the pop-up ---------- */

  function videoEmbed(href) {
    try {
      const url = new URL(href, window.location.origin);
      const host = url.hostname.replace(/^www\./, '');
      let id = '';
      if (host === 'youtu.be') id = url.pathname.slice(1);
      else if (host.endsWith('youtube.com')) id = url.searchParams.get('v') || (url.pathname.match(/\/(embed|shorts)\/([^/?]+)/) || [])[2] || '';
      if (id) return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0`;
      if (host.endsWith('vimeo.com')) {
        const vid = (url.pathname.match(/(\d+)/) || [])[1];
        if (vid) return `https://player.vimeo.com/video/${vid}?autoplay=1`;
      }
    } catch (e) {}
    return '';
  }

  function isInfoLink(link) {
    if (link.hasAttribute('data-mp-info')) return true;
    if (!infoPaths.length) return false;
    let url;
    try {
      url = new URL(link.getAttribute('href'), window.location.origin);
    } catch (e) {
      return false;
    }
    return url.origin === window.location.origin && matches(url.pathname, infoPaths);
  }

  async function loadContent(href) {
    if (cache.has(href)) return cache.get(href);
    const response = await fetch(href, { credentials: 'same-origin', headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(String(response.status));
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const main = doc.querySelector('main') || doc.body;
    const heading = [...main.querySelectorAll('h1')].find((h) => h.textContent.trim()) || null; // skip empty app titles (e.g. the wholesale app's hidden h1)

    // Prefer an explicit content wrapper; otherwise take the page's own container around its <h1>
    // (works for Shopify policy pages and custom page sections alike) and drop the heading itself.
    let body = main.querySelector('[data-mp-info-content], .shopify-policy__body');
    if (!body && heading) {
      const header = heading.closest('header') || heading;
      body = header.parentElement;
      header.remove();
    }
    if (!body || !body.textContent.trim()) body = [...main.querySelectorAll('.rte')].sort((a, b) => b.textContent.trim().length - a.textContent.trim().length)[0]; if (!body || !body.textContent.trim()) throw new Error('No content found');

    body.querySelectorAll('script, style, link, noscript, form, input, button, dialog, [hidden], [aria-hidden="true"]').forEach((el) => el.remove());
    body.querySelectorAll('h1').forEach((el) => el.remove());
    const result = {
      title: heading ? heading.textContent.trim() : doc.title.split(/[–|-]/)[0].trim(),
      html: body.innerHTML,
    };
    cache.set(href, result);
    return result;
  }

  function open(title) {
    titleEl.textContent = title;
    if (!modal.open) {
      modal.showModal();
      document.documentElement.classList.add('mp-info-open');
    }
  }

  function close() {
    if (!modal.open) return;
    modal.close();
    bodyEl.innerHTML = ''; // stop any playing video right away
  }

  modal.addEventListener('close', () => {
    // The close event arrives a moment after closing; skip cleanup if another link already reopened it.
    if (modal.open) return;
    document.documentElement.classList.remove('mp-info-open');
    bodyEl.innerHTML = ''; // stops any playing video
    modal.classList.remove('mp-info--video');
  });

  // Esc closes the dialog natively; clear the content straight away so videos stop.
  modal.addEventListener('cancel', () => {
    bodyEl.innerHTML = '';
  });

  modal.addEventListener('click', (event) => {
    if (event.target === modal || event.target.closest('[data-mp-info-close]')) close();
  });

  async function showLink(link) {
    const href = link.getAttribute('href');
    const label = (link.dataset.mpInfoTitle || link.textContent || '').trim();

    const embed = videoEmbed(href);
    if (embed) {
      modal.classList.add('mp-info--video');
      bodyEl.innerHTML = `<div class="mp-info__video"><iframe src="${embed}" title="${label.replace(/"/g, '&quot;')}" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowfullscreen></iframe></div>`;
      open(label);
      return;
    }

    let url;
    try {
      url = new URL(href, window.location.origin);
    } catch (e) {
      return;
    }
    if (url.origin !== window.location.origin) {
      // An outside page can't be shown in the pop-up; open it in a new tab so the product stays open.
      window.open(url.href, '_blank', 'noopener');
      return;
    }

    bodyEl.innerHTML = '<p class="mp-info__loading">Loading…</p>';
    bodyEl.setAttribute('aria-busy', 'true');
    open(label);
    try {
      const content = await loadContent(url.pathname + url.search);
      titleEl.textContent = content.title || label;
      bodyEl.innerHTML = content.html;
      applyReturnUrls(bodyEl);
      bodyEl.scrollTop = 0; /* Jump to the matching section: #id in the link, or Color Match links go to the Color Match Guarantee section. */ let target = null; if (url.hash) target = bodyEl.querySelector('[id="' + url.hash.slice(1).replace(/"/g, '') + '"]'); if (!target && /color match/i.test((link.closest('p, li') || link).textContent || '')) target = [...bodyEl.querySelectorAll('p, li, h2, h3')].find((el) => /^\s*(\d+\.\s*)?Color Match Guarantee/i.test(el.textContent)); if (target) { target.scrollIntoView({ block: 'start' }); target.style.background = '#e8f6f5'; target.style.borderRadius = '6px'; }
    } catch (error) {
      // If the content can't be loaded, fall back to the normal page.
      close();
      window.location.href = url.href;
    } finally {
      bodyEl.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (!link) return;
    // Let customers open links in a new tab on purpose (Ctrl/Cmd/Shift/middle click).
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!isInfoLink(link)) return;
    event.preventDefault();
    showLink(link);
  });

  const init = () => applyReturnUrls(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', (event) => applyReturnUrls(event.target));
})();
